import { useContext, useEffect, useRef, useState } from "react";
import { Box, Typography, Divider, Tooltip } from "@mui/material";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import LockIcon from "@mui/icons-material/Lock";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";

import { CustomerSelectorContainer } from "./customer_selector/container";
import { ProfileMenu } from "./profile_menu";
import { StartRunButtonContainer } from "./start_run_button/container";
import { TOP_HEADER_HEIGHT } from "./const";
import { useAdmin } from "../../contexts/app/provider";
import { VersionedDataContext } from "../../contexts/versioned_data/context";
import { RunScheduleContainer } from "./run_schedule/container";
import { RunSelectorContainer } from "./run_selector/container";
import { FeedbackModal } from "../feedback_modal";
import { useViewTitleValue } from "../../hooks/use_view_title";
import { useViewActionsSlotRef } from "../../hooks/use_view_actions_slot";

export const TopHeader = () => {
  const { isAdmin, loading: isAppContextLoading } = useAdmin();
  const { loading: isVersionedDataLoading } = useContext(VersionedDataContext);
  const viewTitle = useViewTitleValue();
  const setActionsSlot = useViewActionsSlotRef();
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // Comparing runs isn't meaningful on Inventory Health; hide the control
  // there. startsWith covers the "Inventory Health: Hubs" / ": Spokes" variants
  // too, not just the bare "Inventory Health".
  const isInventoryHealth = Boolean(viewTitle?.startsWith("Inventory Health"));

  const loading = isAppContextLoading || isVersionedDataLoading;

  const headerRef = useRef<HTMLDivElement>(null);
  // Publish the live top-bar height as a CSS var so PageFrame can subtract it.
  // The bar wraps to two lines on narrow widths; this keeps page content sized
  // correctly instead of assuming a fixed header height. The write is deferred
  // to an animation frame and guarded on actual change so the observer can't
  // feed back into a "ResizeObserver loop" warning.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    let frame = 0;
    let lastHeight = -1;
    const apply = () => {
      const height = el.offsetHeight;
      if (height === lastHeight) return;
      lastHeight = height;
      document.documentElement.style.setProperty(
        "--top-header-height",
        `${height}px`
      );
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(apply);
    };
    schedule();
    const observer = new ResizeObserver(schedule);
    observer.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      document.documentElement.style.removeProperty("--top-header-height");
    };
  }, []);

  return (
    <Box
      ref={headerRef}
      sx={{
        p: 2,
        pl: 0,
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        rowGap: 1,
        minHeight: `${TOP_HEADER_HEIGHT}px`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
        {isAdmin && !loading && <CustomerSelectorContainer />}
        {viewTitle && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              minWidth: 0,
              height: 34,
              px: "10px",
              border: "1px solid #c8d8f5",
              borderRadius: "6px",
              backgroundColor: (t) => t.palette.atomic.blue1,
            }}
          >
            <Typography
              noWrap
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: (t) => t.palette.atomic.brandBlueDark,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {viewTitle}
            </Typography>
          </Box>
        )}
        <Box
          ref={setActionsSlot}
          sx={{
            display: "flex",
            alignItems: "center",
            "&:empty": { display: "none" },
          }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        {/* Support tools — apply across every tab, so they live in the header
            rather than the module rail. Sit to the left of the run selector,
            separated by a vertical divider. */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {/* Help is a deliberate placeholder for the docs/help center, which
              isn't ready until modeling goes live. Rendered disabled — no
              handler — with a lock affordance. */}
          <Tooltip title="Help — coming soon">
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                height: 34,
                px: "10px",
                borderRadius: "6px",
                fontSize: "12.5px",
                fontWeight: 500,
                color: "#B8BDC4",
                cursor: "not-allowed",
              }}
            >
              <HelpOutlineRoundedIcon sx={{ fontSize: 18 }} />
              Help
              <LockIcon sx={{ fontSize: 14 }} />
            </Box>
          </Tooltip>

          <Box
            role="button"
            tabIndex={0}
            onClick={() => setFeedbackOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setFeedbackOpen(true);
            }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              height: 34,
              px: "10px",
              borderRadius: "6px",
              fontSize: "12.5px",
              fontWeight: 500,
              cursor: "pointer",
              color: (t) => t.palette.atomic.ink,
              "&:hover": { backgroundColor: "#F4F6F8" },
            }}
          >
            <FeedbackOutlinedIcon sx={{ fontSize: 18 }} />
            Feedback
          </Box>
        </Box>

        <Divider
          orientation="vertical"
          flexItem
          sx={{
            height: 20,
            alignSelf: "center",
            borderColor: (t) => t.palette.atomic.border,
          }}
        />

        <RunScheduleContainer />
        <RunSelectorContainer hideComparison={isInventoryHealth} />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <StartRunButtonContainer />
          <ProfileMenu />
        </Box>
      </Box>

      <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </Box>
  );
};
